import React, { useState, useEffect } from 'react'
import { Container, Input, Button } from 'components/UIElements'
import PropTypes from 'prop-types'
import { PW_REQUIREMENTS } from 'utils/misc'
import { check } from 'assets/assets'
import '../../CreateUser/stylesheets/user.scss'

const ActivateUserPage = props => {
  const {
    changePasswordState,
    changePwErrors,
    setNewPassword,
    updateNewPassword,
    userToken,
    expiration_message,
    isPasswordChange,
  } = props

  const passwordChangeMode = Boolean(isPasswordChange || (!userToken && expiration_message))

  const [isEightChar, setIsEightChar] = useState(false)
  const [hasUppercase, setHasUppercase] = useState(false)
  const [hasLowercase, setHasLowercase] = useState(false)
  const [hasNum, setHasNum] = useState(false)
  const [hasSpecialChar, setHasSpecialChar] = useState(false)
  const [hasSupportedSpecialChar, setHasSupportedSpecialChar] = useState(null)
  const [requestIsPending, setRequestIsPending] = useState(false)

  const validate = password => {
    const isEightCharRegex = /^.{8,}$/g
    const hasUppercaseRegex = /^(.*[A-Z].*)$/g
    const hasLowercaseRegex = /^(.*[a-z].*)$/g
    const hasNumberRegex = /^(.*\d.*)$/g
    const hasSpecialCharRegex = /^(.*[~!@#$%^&*?].*)$/g
    const hasSupportedSpecialCharRegex = /^(.*[^~!@#$%^&*?].*)$/g
    const getSpecialCharacters = password.replace(/[a-z, \d]/gi, '')
    const hasSupportedSpecialCharError = hasSupportedSpecialCharRegex.test(getSpecialCharacters)

    setIsEightChar(isEightCharRegex.test(password))
    setHasUppercase(hasUppercaseRegex.test(password))
    setHasLowercase(hasLowercaseRegex.test(password))
    setHasNum(hasNumberRegex.test(password))
    setHasSupportedSpecialChar(!hasSupportedSpecialCharError)
    setHasSpecialChar(hasSpecialCharRegex.test(password) && !hasSupportedSpecialCharError)
  }

  const getPWReq = reqKey => {
    switch (reqKey) {
      case 'isEightChar':
        return isEightChar
      case 'hasUppercase':
        return hasUppercase
      case 'hasLowercase':
        return hasLowercase
      case 'hasNum':
        return hasNum
      case 'hasSpecialChar':
        return hasSpecialChar
      default:
        return false
    }
  }

  const renderError = _loginError => {
    return <div className='error'>{_loginError}</div>
  }

  const checkIsSubmitButtonDisabled = () => {
    const isDisabled =
      !isEightChar ||
      !hasUppercase ||
      !hasLowercase ||
      !hasNum ||
      !hasSpecialChar ||
      !changePasswordState.password_confirmation.length > 0 ||
      requestIsPending
    if (passwordChangeMode) {
      return isDisabled || !changePasswordState?.old_password?.length > 0
    }

    return isDisabled
  }

  const renderPrompt = msg => <div className='prompt'>{msg}</div>

  const { pwSecurityError, oldPwSecurityErr } = changePwErrors
  const hasSupportedSpecialCharError = typeof hasSupportedSpecialChar === 'boolean' && !hasSupportedSpecialChar

  return (
    <div className='create-user password page'>
      {!userToken && expiration_message && renderPrompt(expiration_message)}
      <Container>
        {pwSecurityError && renderError(pwSecurityError)}
        {oldPwSecurityErr && renderError(oldPwSecurityErr)}
        <h3>Create a secure password</h3>
        <p>Make sure to include</p>
        <ul className='requirements'>
          {PW_REQUIREMENTS.map((req, idx) => {
            return (
              <li key={idx}>
                {check(getPWReq(req.key) ? 'checked' : '')}
                {req.text}
              </li>
            )
          })}
        </ul>
        <form
          onSubmit={e => {
            e.preventDefault()
            setRequestIsPending(true)
            setNewPassword(changePasswordState, passwordChangeMode, () => setRequestIsPending(false))
          }}>
          {passwordChangeMode ? (
            <>
              <label htmlFor='user-password-old'>Old Password</label>
              <Input
                id='user-password-old'
                hasError={!!oldPwSecurityErr || hasSupportedSpecialCharError}
                placeholder='enter old password'
                type='password'
                value={changePasswordState.old_password}
                onChange={value => {
                  validate(value)
                  updateNewPassword('old_password', value)
                  setRequestIsPending(false)
                }}
                errorText={hasSupportedSpecialCharError ? 'Please choose a supported special character' : null}
              />
            </>
          ) : null}

          <label htmlFor='user-password'>New Password</label>
          <Input
            id='user-password'
            hasError={!!pwSecurityError || hasSupportedSpecialCharError}
            placeholder='enter new password'
            type='password'
            value={changePasswordState.password}
            onChange={value => {
              validate(value)
              updateNewPassword('password', value)
              setRequestIsPending(false)
            }}
            errorText={hasSupportedSpecialCharError ? 'Please choose a supported special character' : null}
          />
          <label htmlFor='user-password'>Retype New Password</label>
          <Input
            id='user-password-confirmation'
            hasError={!!pwSecurityError}
            placeholder='re-enter password'
            type='password'
            value={changePasswordState.password_confirmation}
            onChange={value => {
              updateNewPassword('password_confirmation', value)
              setRequestIsPending(false)
            }}
          />
          <Button type='submit' content='Set Password' disabled={checkIsSubmitButtonDisabled()} />
        </form>
      </Container>
    </div>
  )
}

export default ActivateUserPage

ActivateUserPage.propTypes = {
  changePasswordState: PropTypes.shape({
    password: PropTypes.string,
    password_confirmation: PropTypes.string,
  }),
  changePwErrors: PropTypes.objectOf(PropTypes.string),
  expiration_message: PropTypes.string,
  isPasswordChange: PropTypes.bool,
  setNewPassword: PropTypes.func,
  updateNewPassword: PropTypes.func,
  userToken: PropTypes.string,
}
